import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import Header from '../../components/ui/Header';
import MobileBottomNav from '../../components/ui/MobileBottomNav';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import ProductGrid from './components/ProductGrid';
import ProductFormModal from './components/ProductFormModal';
import CategoryManagement from './components/CategoryManagement';
import InventoryAlerts from './components/InventoryAlerts';
import BulkImportModal from './components/BulkImportModal';
import InventorySync from './components/InventorySync';

const ProductCatalogManagement = () => {
  const navigate = useNavigate();
  const { user, currentTenant, isGymOwner, isStaff, loading: authLoading } = useAuth();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showProductModal, setShowProductModal] = useState(false);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [showBulkImportModal, setShowBulkImportModal] = useState(false);
  const [stats, setStats] = useState({
    totalProducts: 0,
    activeProducts: 0,
    lowStockItems: 0,
    totalValue: 0
  });

  useEffect(() => {
    if (!authLoading && (!user || (!isGymOwner && !isStaff))) {
      navigate('/member-dashboard');
    }
  }, [user, isGymOwner, isStaff, authLoading, navigate]);

  useEffect(() => {
    if (currentTenant?.id && (isGymOwner || isStaff)) {
      loadCatalogData();
    }
  }, [currentTenant, isGymOwner, isStaff, filterCategory, filterStatus, searchQuery]);

  const loadCatalogData = async () => {
    setLoading(true);
    try {
      // Load categories
      const { data: categoriesData, error: categoriesError } = await supabase
        ?.from('pos_categories')
        ?.select('*')
        ?.eq('tenant_id', currentTenant?.id)
        ?.eq('is_active', true)
        ?.order('display_order', { ascending: true });

      if (categoriesError) throw categoriesError;
      setCategories(categoriesData || []);

      // Load products
      let query = supabase
        ?.from('pos_products')
        ?.select('*, pos_categories(name), user_profiles:created_by(full_name)')
        ?.eq('tenant_id', currentTenant?.id)
        ?.order('created_at', { ascending: false });

      if (filterCategory !== 'all') {
        query = query?.eq('category_id', filterCategory);
      }

      if (filterStatus !== 'all') {
        query = query?.eq('is_active', filterStatus === 'active');
      }

      if (searchQuery?.trim()) {
        query = query?.or(`name.ilike.%${searchQuery}%,sku.ilike.%${searchQuery}%,barcode.ilike.%${searchQuery}%`);
      }

      const { data: productsData, error: productsError } = await query;

      if (productsError) throw productsError;
      setProducts(productsData || []);

      // Calculate stats
      const totalProducts = productsData?.length || 0;
      const activeProducts = productsData?.filter(p => p?.is_active)?.length || 0;
      const lowStockItems = productsData?.filter(p => p?.track_inventory && p?.stock_quantity <= p?.low_stock_threshold)?.length || 0;
      const totalValue = productsData?.reduce((sum, p) => sum + (p?.stock_quantity * p?.price), 0) || 0;

      setStats({
        totalProducts,
        activeProducts,
        lowStockItems,
        totalValue
      });

    } catch (error) {
      console.error('Load catalog error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateProduct = () => {
    setSelectedProduct(null);
    setShowProductModal(true);
  };

  const handleEditProduct = (product) => {
    setSelectedProduct(product);
    setShowProductModal(true);
  };

  const handleDeleteProduct = async (productId) => {
    if (!confirm('Are you sure you want to delete this product?')) return;

    try {
      const { error } = await supabase
        ?.from('pos_products')
        ?.update({ is_active: false })
        ?.eq('id', productId);

      if (error) throw error;
      loadCatalogData();
    } catch (error) {
      console.error('Delete product error:', error);
      alert('Failed to delete product');
    }
  };

  const handleProductSaved = () => {
    setShowProductModal(false);
    setSelectedProduct(null);
    loadCatalogData();
  };

  const handleCategorySaved = () => {
    setShowCategoryModal(false);
    loadCatalogData();
  };

  const handleBulkImportComplete = () => {
    setShowBulkImportModal(false);
    loadCatalogData();
  };

  const handleInventoryStockUpdate = (updatedProduct, action) => {
    if (action === 'delete') {
      setProducts(prev => prev?.filter(p => p?.id !== updatedProduct?.id));
    } else {
      setProducts(prev => {
        const index = prev?.findIndex(p => p?.id === updatedProduct?.id);
        if (index !== -1) {
          const updated = [...prev];
          updated[index] = { ...updated?.[index], ...updatedProduct };
          return updated;
        } else {
          return [updatedProduct, ...prev];
        }
      });
    }
    
    // Recalculate stats
    setProducts(currentProducts => {
      const totalProducts = currentProducts?.length || 0;
      const activeProducts = currentProducts?.filter(p => p?.is_active)?.length || 0;
      const lowStockItems = currentProducts?.filter(p => p?.track_inventory && p?.stock_quantity <= p?.low_stock_threshold)?.length || 0;
      const totalValue = currentProducts?.reduce((sum, p) => sum + (p?.stock_quantity * p?.price), 0) || 0;
      
      setStats({
        totalProducts,
        activeProducts,
        lowStockItems,
        totalValue
      });
      
      return currentProducts;
    });
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Icon name="Loader2" size={48} className="text-primary animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">Loading catalog...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />

      <main className="container mx-auto px-4 py-6 pb-24 lg:pb-6">
        {/* Page Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-2">
            <h1 className="text-3xl font-bold text-foreground">Product Catalog</h1>
            <div className="flex items-center gap-2">
              <InventorySync 
                tenantId={currentTenant?.id}
                onStockUpdate={handleInventoryStockUpdate}
              />
              <Button
                variant="ghost"
                onClick={() => setShowCategoryModal(true)}
              >
                <Icon name="FolderTree" size={18} className="mr-2" />
                Categories
              </Button>
              <Button
                variant="outline"
                onClick={() => setShowBulkImportModal(true)}
              >
                <Icon name="Upload" size={18} className="mr-2" />
                Bulk Import
              </Button>
              <Button onClick={handleCreateProduct}>
                <Icon name="Plus" size={18} className="mr-2" />
                Add Product
              </Button>
            </div>
          </div>
          <p className="text-muted-foreground">
            Manage your gym's product inventory and catalog
          </p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-card rounded-lg p-4 border border-border">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-muted-foreground">Total Products</span>
              <Icon name="Package" size={20} className="text-primary" />
            </div>
            <p className="text-2xl font-bold text-foreground">{stats?.totalProducts}</p>
          </div>

          <div className="bg-card rounded-lg p-4 border border-border">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-muted-foreground">Active Products</span>
              <Icon name="CheckCircle" size={20} className="text-success" />
            </div>
            <p className="text-2xl font-bold text-foreground">{stats?.activeProducts}</p>
          </div>

          <div className="bg-card rounded-lg p-4 border border-border">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-muted-foreground">Low Stock Alerts</span>
              <Icon name="AlertTriangle" size={20} className="text-warning" />
            </div>
            <p className="text-2xl font-bold text-foreground">{stats?.lowStockItems}</p>
          </div>

          <div className="bg-card rounded-lg p-4 border border-border">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-muted-foreground">Inventory Value</span>
              <Icon name="DollarSign" size={20} className="text-accent" />
            </div>
            <p className="text-2xl font-bold text-foreground">${stats?.totalValue?.toFixed(2)}</p>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-card rounded-lg p-4 border border-border mb-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Search */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Search Products
              </label>
              <div className="relative">
                <Icon name="Search" size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e?.target?.value)}
                  placeholder="Name, SKU, or barcode..."
                  className="w-full pl-10 pr-4 py-2 rounded-lg bg-background border border-border focus:outline-none focus:ring-2 focus:ring-primary text-foreground"
                />
              </div>
            </div>

            {/* Category Filter */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Category
              </label>
              <select
                value={filterCategory}
                onChange={(e) => setFilterCategory(e?.target?.value)}
                className="w-full px-4 py-2 rounded-lg bg-background border border-border focus:outline-none focus:ring-2 focus:ring-primary text-foreground"
              >
                <option value="all">All Categories</option>
                {categories?.map((cat) => (
                  <option key={cat?.id} value={cat?.id}>{cat?.name}</option>
                ))}
              </select>
            </div>

            {/* Status Filter */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Status
              </label>
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e?.target?.value)}
                className="w-full px-4 py-2 rounded-lg bg-background border border-border focus:outline-none focus:ring-2 focus:ring-primary text-foreground"
              >
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="out_of_stock">Out of Stock</option>
                <option value="discontinued">Discontinued</option>
              </select>
            </div>
          </div>
        </div>

        {/* Inventory Alerts */}
        {stats?.lowStockItems > 0 && (
          <InventoryAlerts
            products={products?.filter(p => p?.track_inventory && p?.stock_quantity <= p?.low_stock_threshold)}
            onRestock={(product) => handleEditProduct(product)}
          />
        )}

        {/* Product Grid */}
        <ProductGrid
          products={products}
          onEdit={handleEditProduct}
          onDelete={handleDeleteProduct}
        />
      </main>

      {/* Product Form Modal */}
      {showProductModal && (
        <ProductFormModal
          product={selectedProduct}
          categories={categories}
          tenantId={currentTenant?.id}
          userId={user?.id}
          onClose={() => setShowProductModal(false)}
          onSaved={handleProductSaved}
        />
      )}

      {/* Category Management Modal */}
      {showCategoryModal && (
        <CategoryManagement
          categories={categories}
          tenantId={currentTenant?.id}
          isGymOwner={isGymOwner}
          isStaff={isStaff}
          onClose={() => setShowCategoryModal(false)}
          onSaved={handleCategorySaved}
        />
      )}

      {/* Bulk Import Modal */}
      {showBulkImportModal && (
        <BulkImportModal
          tenantId={currentTenant?.id}
          userId={user?.id}
          categories={categories}
          onClose={() => setShowBulkImportModal(false)}
          onImportComplete={handleBulkImportComplete}
        />
      )}

      <MobileBottomNav />
    </div>
  );
};

export default ProductCatalogManagement;